// src/App.js - Turf Manager Main Application

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './App.css';

// Components
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import Notifications from './components/Notifications';
import TurfBoxTable from './components/TurfBoxTable';
import SensorMap from './components/SensorMap';
import Reports from './components/Reports';
import DataLoader from './components/DataLoader';
import HeatingList from './components/HeatingList';
import IrrigationList from './components/IrrigationList';
import UnitList from './components/UnitList';
import UnitDetail from './components/UnitDetail';

// Services & Context
import ExcelReader from './services/ExcelReader';
import { useLanguage } from './contexts/LanguageContext';
import { excelFileManifest, fetchFolderManifest } from './data/excelFileManifest';

function App() {
  const [data, setData] = useState([]);
  const [temperatureSeries, setTemperatureSeries] = useState([]);
  const [humiditySeries, setHumiditySeries] = useState([]);
  const [illuminationSeries, setIlluminationSeries] = useState([]);
  const [turfBoxFiles, setTurfBoxFiles] = useState(excelFileManifest.TurfBox);
  const [deviceIds, setDeviceIds] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitRowCount, setUnitRowCount] = useState(null);
  const [heatingFiles, setHeatingFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';

  const folderDeviceCounts = useMemo(() => ({
    turfBox: deviceIds.length || excelFileManifest.TurfBox.length,
    unite: excelFileManifest.Unite.length,
    sulama: excelFileManifest.Sulama.length,
    isitma: heatingFiles.length
  }), [deviceIds.length, heatingFiles.length]);

  const notifications = useMemo(() => ([
    {
      id: 1,
      type: 'warning',
      message: t('notifications.lowMoistureDetected', { count: 3 }),
      time: t('notifications.minutesAgo', { count: 5 }),
      unread: true
    },
    {
      id: 2,
      type: 'info',
      message: t('notifications.newDataLoaded', { count: data.length.toLocaleString(locale) }),
      time: t('notifications.minutesAgo', { count: 10 }),
      unread: true
    },
    {
      id: 3,
      type: 'success',
      message: t('notifications.systemUpdateComplete'),
      time: t('notifications.hoursAgo', { count: 1 }),
      unread: false
    }
  ]), [data.length, locale, t]);

  const extractDeviceIds = useCallback((fileNames) => {
    const devices = new Set();
    fileNames
      .map((fileName) => fileName.split('/').pop())
      .filter(Boolean)
      .forEach((fileName) => {
        const match = fileName.match(/^Cihaz-(\d+)\.xlsx$/i);
        if (match?.[1]) {
          devices.add(match[1]);
        }
      });
    return Array.from(devices).sort();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTurfBoxFiles = async () => {
      const files = await fetchFolderManifest('TurfBox');
      const resolvedFiles = files.length > 0 ? files : excelFileManifest.TurfBox;
      const nextDeviceIds = extractDeviceIds(resolvedFiles);

      if (isMounted) {
        setTurfBoxFiles(resolvedFiles);
        setDeviceIds(nextDeviceIds);
        setSelectedDeviceId((previousId) => (
          nextDeviceIds.includes(previousId) ? previousId : (nextDeviceIds[0] ?? null)
        ));
      }
    };

    loadTurfBoxFiles();

    return () => {
      isMounted = false;
    };
  }, [extractDeviceIds]);

  useEffect(() => {
    let isMounted = true;

    const loadHeatingFiles = async () => {
      const files = await fetchFolderManifest('Isitma');
      if (isMounted) {
        setHeatingFiles(files);
      }
    };

    loadHeatingFiles();

    return () => {
      isMounted = false;
    };
  }, []);

  const resolveDeviceId = (fileNames) => {
    const normalizedFiles = fileNames
      .map((fileName) => fileName.split('/').pop())
      .filter(Boolean);
    const deviceMatch = normalizedFiles
      .map((fileName) => fileName.match(/^Cihaz-(\d+)/i))
      .find(Boolean);
    return deviceMatch?.[1] || '001';
  };

  const resolveTemperatureFileName = (deviceId, fileNames) => {
    const normalizedFiles = fileNames
      .map((fileName) => fileName.split('/').pop())
      .filter(Boolean);
    const preferredFile = `${deviceId}-Temp.xlsx`;
    if (normalizedFiles.includes(preferredFile)) {
      return preferredFile;
    }

    const legacyTempFile = `Cihaz-${deviceId}-Temp-C.xlsx`;
    if (normalizedFiles.includes(legacyTempFile)) {
      return legacyTempFile;
    }

    const deviceDataFile = `Cihaz-${deviceId}.xlsx`;
    if (normalizedFiles.includes(deviceDataFile)) {
      return preferredFile;
    }

    return preferredFile;
  };

  const resolveSeriesFileName = (deviceId, fileNames, candidates) => {
    const normalizedFiles = fileNames
      .map((fileName) => fileName.split('/').pop())
      .filter(Boolean);
    for (const candidate of candidates) {
      if (normalizedFiles.includes(candidate)) {
        return candidate;
      }
    }
    return candidates[0];
  };

  const loadExcelData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // public/data/ klasöründeki Excel dosyaları
      const fileList = turfBoxFiles;
      const deviceId = selectedDeviceId || resolveDeviceId(fileList);
      const temperatureFileName = resolveTemperatureFileName(deviceId, fileList);
      const humidityFileName = resolveSeriesFileName(deviceId, fileList, [
        `${deviceId}-Humidity.xlsx`,
        `Cihaz-${deviceId}-Humidity.xlsx`
      ]);
      const illuminationFileName = resolveSeriesFileName(deviceId, fileList, [
        `${deviceId}-Illimunation.xlsx`,
        `${deviceId}-Illumination.xlsx`,
        `Cihaz-${deviceId}-Illimunation.xlsx`,
        `Cihaz-${deviceId}-Illumination.xlsx`
      ]);

      console.log('📂 Excel dosyaları yükleniyor...');
      const [result, uniteResult, temperatureResult, humidityResult, illuminationResult] = await Promise.all([
        ExcelReader.readAllFiles(fileList),
        ExcelReader.readUniteFile('Unite-001.xlsx', 'Unite'),
        ExcelReader.readTemperatureTimeSeries(temperatureFileName, 'TurfBox'),
        ExcelReader.readHumidityTimeSeries(humidityFileName, 'TurfBox'),
        ExcelReader.readIlluminationTimeSeries(illuminationFileName, 'TurfBox')
      ]);

      setUnitRowCount(uniteResult.success ? uniteResult.data.length : 0);

      if (temperatureResult?.success) {
        const formatTemperatureC = (value) => {
          const numericValue = Number(value);
          if (Number.isNaN(numericValue)) {
            return null;
          }
          const fixed = Math.abs(numericValue).toFixed(2);
          const [integerPart, decimalPart] = fixed.split('.');
          const trimmedInteger = integerPart.length > 2 ? integerPart.slice(0, 2) : integerPart;
          const trimmedValue = Number(`${trimmedInteger}.${decimalPart}`);
          return Number.isNaN(trimmedValue)
            ? null
            : (numericValue < 0 ? -trimmedValue : trimmedValue);
        };

        const mappedSeries = temperatureResult.data
          .filter((entry) => entry.timestamp && !Number.isNaN(entry.temperatureC))
          .map((entry) => {
            const temperatureC = formatTemperatureC(entry.temperatureC);
            return {
              ...entry,
              temperatureC
            };
          })
          .filter((entry) => entry.temperatureC !== null);

        setTemperatureSeries(mappedSeries);
      } else {
        setTemperatureSeries([]);
      }

      if (humidityResult?.success) {
        setHumiditySeries(
          humidityResult.data
            .filter((entry) => entry.timestamp && !Number.isNaN(entry.humidity))
        );
      } else {
        setHumiditySeries([]);
      }

      if (illuminationResult?.success) {
        setIlluminationSeries(
          illuminationResult.data
            .filter((entry) => entry.timestamp && !Number.isNaN(entry.illumination))
        );
      } else {
        setIlluminationSeries([]);
      }

      if (result.success && result.data.length > 0) {
        console.log(`✅ ${result.data.length} kayıt yüklendi`);
        console.log(`📊 ${result.successfulReads}/${result.totalFiles} dosya başarıyla okundu`);

        setData(result.data);
        setLoading(false);
      } else {
        throw new Error(t('dataLoader.loadErrorEmpty'));
      }

    } catch (err) {
      console.error('❌ Veri yükleme hatası:', err);
      setError(err.message || t('dataLoader.loadError'));
      setUnitRowCount(0);
      setTemperatureSeries([]);
      setHumiditySeries([]);
      setIlluminationSeries([]);
      setLoading(false);
    }
  }, [selectedDeviceId, t, turfBoxFiles]);

  // Excel dosyalarını yükle
  useEffect(() => {
    loadExcelData();
  }, [loadExcelData]);

  const handleOpenSensorMap = (zoneId) => {
    setSelectedZoneId(zoneId);
    setActiveView('map');
  };

  const handleOpenUnitDetail = (unit) => {
    setSelectedUnit(unit);
    setActiveView('unit-detail');
  };

  const handleBackToUnits = () => {
    setActiveView('units');
  };

  // Yükleme durumu
  if (loading) {
    return <DataLoader message={null} onRetry={loadExcelData} />;
  }

  // Hata durumu
  if (error) {
    return <DataLoader message={error} onRetry={loadExcelData} />;
  }

  // Ana uygulama
  return (
    <div className="App min-h-screen bg-gray-50">
      <Navigation
        activeView={activeView}
        setActiveView={setActiveView}
        turfBoxRecordCount={data.length}
        folderDeviceCounts={folderDeviceCounts}
        notifications={notifications}
      />

      <main className="max-w-7xl mx-auto px-6 py-6">
        {activeView === 'dashboard' && (
          <Dashboard
            data={data}
            setActiveView={setActiveView}
            folderDeviceCounts={folderDeviceCounts}
            unitRowCount={unitRowCount}
            temperatureSeries={temperatureSeries}
            humiditySeries={humiditySeries}
            illuminationSeries={illuminationSeries}
            deviceIds={deviceIds}
            selectedDeviceId={selectedDeviceId}
            onSelectDevice={setSelectedDeviceId}
          />
        )}
        {activeView === 'table' && (
          <TurfBoxTable data={data} onSelectZone={handleOpenSensorMap} />
        )}
        {activeView === 'map' && <SensorMap data={data} selectedZoneId={selectedZoneId} />}
        {activeView === 'reports' && <Reports />}
        {activeView === 'notifications' && <Notifications notifications={notifications} />}
        {activeView === 'heating' && <HeatingList />}
        {activeView === 'irrigation' && <IrrigationList />}
        {activeView === 'units' && (
          <UnitList onSelectUnit={handleOpenUnitDetail} />
        )}
        {activeView === 'unit-detail' && (
          <UnitDetail
            unit={selectedUnit}
            onBack={handleBackToUnits}
            onOpenSensorMap={handleOpenSensorMap}
          />
        )}
      </main>
    </div>
  );
}

export default App;
